import { createTRPCReact } from '@trpc/react-query'
import { httpBatchLink, loggerLink } from '@trpc/client'
import type { AppRouter } from '@server/routers/index'
import { useAuthStore } from '../stores/auth.store'
import { isDebugMode } from './debug'

export const trpc = createTRPCReact<AppRouter>()

export function createTrpcClient() {
  return trpc.createClient({
    links: [
      loggerLink({ enabled: () => isDebugMode() }),
      httpBatchLink({
        url: '/trpc',
        headers: () => {
          const token = useAuthStore.getState().accessToken
          return token ? { Authorization: `Bearer ${token}` } : {}
        },
        fetch: async (url, options) => {
          const res = await fetch(url, options)
          if (res.status === 401) {
            const refreshed = await attemptTokenRefresh()
            if (refreshed) {
              const newToken = useAuthStore.getState().accessToken
              return fetch(url, {
                ...options,
                headers: {
                  ...(options?.headers as Record<string, string>),
                  Authorization: `Bearer ${newToken}`,
                },
              })
            } else {
              useAuthStore.getState().clearAuth()
              window.location.href = '/app/login'
            }
          }
          return res
        },
      }),
    ],
  })
}

// Promesse inflight singleton : des 401 concurrents (plusieurs requêtes tRPC en
// parallèle) ne doivent déclencher qu'UN seul refresh — sinon plusieurs tokens
// successifs sont mintés, ce qui change l'identité d'accessToken plusieurs fois de
// suite et amplifie le churn de connexion Centrifugo (cf. debug-churn-connexion-centrifugo).
let inflightRefresh: Promise<boolean> | null = null

export async function attemptTokenRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh

  inflightRefresh = doTokenRefresh()
  try {
    return await inflightRefresh
  } finally {
    // Relâché dans tous les cas (succès ET échec) : un refresh légitime ultérieur
    // ne doit jamais rester bloqué derrière une tentative résolue.
    inflightRefresh = null
  }
}

async function doTokenRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/auth/refresh', { method: 'POST', credentials: 'include' })
    if (!res.ok) return false
    const { accessToken } = (await res.json()) as { accessToken: string }
    // Mettre a jour le token ET le user depuis le JWT (role peut avoir change)
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1])) as {
        sub: string; email: string; name?: string; role?: string
      }
      useAuthStore.getState().setAuth(
        { id: payload.sub, email: payload.email, name: payload.name ?? '', role: (payload.role ?? 'user') as 'admin' | 'user' },
        accessToken,
      )
    } catch {
      useAuthStore.getState().updateToken(accessToken)
    }
    return true
  } catch {
    return false
  }
}
